import { EditOutlined, LightbulbOutlined } from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Input } from '@/components/common/Input';
import {
  useTransactionLimits,
  useUpdateTransactionLimits,
} from '@/hooks/queries/useOrganizationConfig';

export const TransactionLimitsCard: React.FC = () => {
  const { data: limits, isLoading } = useTransactionLimits();
  const updateLimits = useUpdateTransactionLimits();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    dailyTransactionLimit: null as number | null,
    monthlyTransactionLimit: null as number | null,
    transactionLimitPerOperation: null as number | null,
  });

  const handleEdit = () => {
    if (limits) {
      setFormData({
        dailyTransactionLimit: limits.dailyTransactionLimit,
        monthlyTransactionLimit: limits.monthlyTransactionLimit,
        transactionLimitPerOperation: limits.transactionLimitPerOperation,
      });
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      formData.dailyTransactionLimit !== null &&
      formData.monthlyTransactionLimit !== null &&
      formData.dailyTransactionLimit > formData.monthlyTransactionLimit
    ) {
      toast.error('El límite diario no puede ser mayor al límite mensual');
      return;
    }

    if (
      formData.dailyTransactionLimit !== null &&
      formData.transactionLimitPerOperation !== null &&
      formData.transactionLimitPerOperation > formData.dailyTransactionLimit
    ) {
      toast.error('El límite por operación no puede ser mayor al límite diario');
      return;
    }

    try {
      await updateLimits.mutateAsync(formData);
      toast.success('Cambio enviado a revisión. Un super admin debe aprobarlo antes de aplicarse.');
      setIsEditing(false);
    } catch (_error) {
      toast.error('Error al actualizar los límites');
    }
  };

  if (isLoading) {
    return (
      <CardContainer>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-white/10 rounded w-1/3"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
            <div className="h-4 bg-white/10 rounded w-1/2"></div>
          </div>
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white">Límites de Transacción</h3>
            <p className="text-sm text-muted mt-1">
              Controla los límites de transacciones para empleados
            </p>
          </div>
          {!isEditing && (
            <Button variant="white" size="small" onClick={handleEdit}>
              <EditOutlined fontSize="small" className="mr-1" />
              Editar
            </Button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
            <div>
              <Input
                id="dailyTransactionLimit"
                label="Límite Diario de Transacciones"
                type="number"
                value={formData.dailyTransactionLimit || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    dailyTransactionLimit: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                min="0"
                placeholder="50000"
                disabled={updateLimits.isPending}
              />
              <p className="text-xs text-muted mt-1">
                Total máximo de transacciones por día. Deja vacío para sin límite (no recomendado).
              </p>
            </div>

            <div>
              <Input
                id="monthlyTransactionLimit"
                label="Límite Mensual de Transacciones"
                type="number"
                value={formData.monthlyTransactionLimit || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    monthlyTransactionLimit: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                min="0"
                placeholder="200000"
                disabled={updateLimits.isPending}
              />
              <p className="text-xs text-muted mt-1">
                Total máximo de transacciones por mes. Deja vacío para sin límite (no recomendado).
              </p>
            </div>

            <div>
              <Input
                id="transactionLimitPerOperation"
                label="Límite por Operación Individual"
                type="number"
                value={formData.transactionLimitPerOperation || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    transactionLimitPerOperation: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                min="0"
                placeholder="10000"
                disabled={updateLimits.isPending}
              />
              <p className="text-xs text-muted mt-1">
                Monto máximo por transacción individual. Deja vacío para sin límite (no
                recomendado).
              </p>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <LightbulbOutlined className="text-brand-blue mt-0.5" fontSize="small" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-brand-white mb-1">Valores Recomendados:</p>
                  <ul className="text-xs text-muted space-y-0.5">
                    <li>• Diario: $50,000</li>
                    <li>• Mensual: $200,000</li>
                    <li>• Por Operación: $10,000</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" variant="black" size="small" disabled={updateLimits.isPending}>
                {updateLimits.isPending ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
              <Button
                type="button"
                variant="white"
                size="small"
                onClick={handleCancel}
                disabled={updateLimits.isPending}
              >
                Cancelar
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-muted mb-1">Límite Diario</p>
                <p className="text-2xl font-bold text-brand-white">
                  {limits?.dailyTransactionLimit
                    ? `$${limits.dailyTransactionLimit.toLocaleString()}`
                    : 'Sin límite'}
                </p>
              </div>

              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-muted mb-1">Límite Mensual</p>
                <p className="text-2xl font-bold text-brand-white">
                  {limits?.monthlyTransactionLimit
                    ? `$${limits.monthlyTransactionLimit.toLocaleString()}`
                    : 'Sin límite'}
                </p>
              </div>

              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-muted mb-1">Límite por Operación</p>
                <p className="text-2xl font-bold text-brand-white">
                  {limits?.transactionLimitPerOperation
                    ? `$${limits.transactionLimitPerOperation.toLocaleString()}`
                    : 'Sin límite'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </CardContainer>
  );
};
